import type { FetchArgType } from "openapi-typescript-fetch";
import type {
    GetListParams,
    GetListResult,
    GetManyReferenceParams,
    GetManyReferenceResult,
    UpdateParams,
    UpdateManyParams,
    CreateParams,
    DeleteManyParams,
    DeleteManyResult,
    UpdateResult,
    GetManyResult,
    CreateResult
} from "react-admin";
import { isEmpty } from "lodash";

import { fetcher } from "../../data";
import { isDefined } from "../../utils/typedLoDashUtils";
import { assertIsDefined } from "../../utils/assertions";
import type { BaseFilters } from "../../data/BaseFilters";
import { NotImplementedError } from "../../utils/NotImplementedError";
import type { ErrorDetails, LangParams, MpmDataProvider, MpmDataProviderOperationName } from "../../data";

import type { RightHolderDTO } from "./RightHolderDTO";
import type { RightHolderRecord } from "./RightHolderRecord";

const findAll = fetcher.path("/api/legal/right-holder/findAll").method("post").create();
const getOne = fetcher.path("/api/legal/right-holder/{uuid}").method("get").create();
const updateOne = fetcher.path("/api/legal/right-holder/{uuid}").method("put").create();
const createOne = fetcher.path("/api/legal/right-holder/").method("post").create();
const deleteOne = fetcher.path("/api/legal/right-holder/{uuid}").method("delete").create();
const getAllByUuids = fetcher.path("/api/legal/right-holder/allByUuids").method("get").create();

/**
 * Parses the DTO from backend to record for frontend
 * @param dto
 * @returns UserRecord
 */

function mapDTOToRecord(dto: RightHolderDTO): RightHolderRecord {
    // DTO from backend
    const { shortId = 0, uuid = "", name = "", description = "", licenseDuration } = dto;

    // Record for front-end
    return {
        shortId,
        id: uuid,
        uuid,
        name,
        description,
        licenseDuration
    };
}

/**
 * Parses the record from frontend to DTO for backend
 * @param record
 * @returns FormatDTO
 */

function mapRecordToDTO(record: RightHolderRecord): RightHolderDTO {
    // Record from front-end
    const { shortId = 0, uuid = "", name = "", description = "", licenseDuration } = record;

    // DTO for back-end
    return {
        id: shortId,
        name,
        uuid,
        description,
        licenseDuration
    };
}

interface UserFilters extends BaseFilters {
    q?: string;
    email: string;
}

async function getList(params: Readonly<GetListParams>): Promise<GetListResult<RightHolderRecord>> {
    const { q: searchTerms, ...filters } = params.filter as UserFilters;

    let parameters: FetchArgType<typeof findAll> = {
        filter: filters,
        pageable: {
            page: params.pagination.page - 1,
            size: params.pagination.perPage,
            sort: isEmpty(params.sort.field) ? [] : [`${params.sort.field},${params.sort.order}`]
        }
    } as const;

    if (isDefined(searchTerms)) {
        parameters = {
            ...parameters,
            filter: { ...parameters.filter, searchTerms }
        };
    }

    const { data } = await findAll(parameters);
    const dataPage = data.content;
    assertIsDefined(dataPage);
    const total = dataPage.totalElements;
    assertIsDefined(total);
    assertIsDefined(dataPage.content);

    return {
        data: dataPage.content.map((content) => mapDTOToRecord({ id: content.id ?? 0, ...content })),
        total
    };
}

async function _getOne(params: LangParams | (number | string)): Promise<RightHolderRecord> {
    const {
        data: { content: rightHolderDTO }
    } = await getOne({
        uuid: params as string
    });
    assertIsDefined(rightHolderDTO);

    return mapDTOToRecord({ id: rightHolderDTO.id ?? 0, ...rightHolderDTO });
}

async function getMany(uuids: readonly (number | string)[]): Promise<GetManyResult<RightHolderRecord>> {
    const {
        data: { content: rightHolderDTO }
    } = await getAllByUuids({
        uuids: uuids as string[]
    });
    assertIsDefined(rightHolderDTO);

    return { data: await Promise.all(rightHolderDTO.map((dto) => mapDTOToRecord({ id: dto.id ?? 0, ...dto }))) };
}

async function getManyReference(_params: Readonly<GetManyReferenceParams>): Promise<GetManyReferenceResult<RightHolderRecord>> {
    return Promise.reject(new NotImplementedError("getManyReference"));
}

async function update(params: Readonly<UpdateParams<RightHolderRecord>>): Promise<UpdateResult<RightHolderRecord>> {
    const payload = mapRecordToDTO(params.data as RightHolderRecord);

    const parameters = { uuid: payload.uuid ?? "", ...payload };

    const { data } = await updateOne(parameters);
    const rightHolderDTO = data.content;
    assertIsDefined(rightHolderDTO);

    return { data: mapDTOToRecord({ id: parameters.id, ...rightHolderDTO }) };
}

async function updateMany(_params: Readonly<UpdateManyParams>): Promise<string[]> {
    return Promise.reject(new NotImplementedError("updateMany"));
}

async function create(params: Readonly<CreateParams>): Promise<CreateResult<RightHolderRecord>> {
    const payload = mapRecordToDTO(params.data as any);

    assertIsDefined(payload.name);
    const parameters = { ...payload };
    delete (parameters as any)["id"];
    delete (parameters as any)["uuid"];
    const { data } = await createOne(parameters);
    const rightHolderDTO = data.content;

    assertIsDefined(rightHolderDTO);

    return { data: mapDTOToRecord({ id: rightHolderDTO.id as number, ...rightHolderDTO }) };
}

// eslint-disable-next-line no-underscore-dangle
async function _delete(uuid: number | string): Promise<RightHolderRecord> {
    const {
        data: { content: isDeleted }
    } = await deleteOne({
        uuid: uuid as string
    });
    assertIsDefined(isDeleted);

    return {} as RightHolderRecord;
}

async function deleteMany(_params: Readonly<DeleteManyParams>): Promise<DeleteManyResult> {
    return Promise.reject(new NotImplementedError("deleteMany"));
}

function getErrorDetails(_error: unknown, _operation: MpmDataProviderOperationName<RightHolderRecord>): ErrorDetails | undefined {
    // eslint-disable-next-line no-undefined
    return undefined;
}

export const RightHolderDataProvider: MpmDataProvider<RightHolderRecord> = {
    create,
    delete: _delete,
    deleteMany,
    getErrorDetails,
    getList,
    getMany,
    getManyReference,
    getOne: _getOne,
    update,
    updateMany
};
